/* eslint-disable */
// Generated by scripts/vendor-content-parser.mjs from @libpdf/core 0.4.2.
// Source SHA-256: d36d42ce65da31953ec32bf4db249d703d786379c3bf99e71057790b8befa29d
// MIT, copyright 2026 Lucas Smith. See libpdf-LICENSE.txt.
// Scrivar modification: exact raw/Flate/RLE inline image boundaries.
import { exactInlineImageEnd } from '../pdfInlineImageLength';
// Inline-image handling derives from PDF.js (Apache-2.0); see pdfjs-LICENSE.txt.
//#region src/helpers/buffer.ts
/**
* Buffer utilities for working with ArrayBuffer and Uint8Array.
*/
/**
* Ensure we have a proper ArrayBuffer (not SharedArrayBuffer or slice).
*
* Web Crypto APIs require a true ArrayBuffer, not a view into one.
*
* @param data - Uint8Array to convert
* @returns ArrayBuffer containing the data
*/
function toArrayBuffer(data) {
	if (data.buffer instanceof ArrayBuffer && data.byteOffset === 0 && data.byteLength === data.buffer.byteLength) return data.buffer;
	return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
}
/**
* Concatenate multiple Uint8Arrays into a single Uint8Array.
*
* @param arrays - Arrays to concatenate
* @returns Single Uint8Array containing all data
*/
function concatBytes(arrays) {
	const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
	const result = new Uint8Array(totalLength);
	let offset = 0;
	for (const arr of arrays) {
		result.set(arr, offset);
		offset += arr.length;
	}
	return result;
}
/** Pre-computed hex lookup: byte value → "XX" uppercase string. */
const HEX_TABLE = new Array(256);
for (let i = 0; i < 256; i++) HEX_TABLE[i] = i.toString(16).toUpperCase().padStart(2, "0");
/**
* Convert bytes to uppercase hex string.
*
* @param bytes - Raw bytes
* @returns Hex string (e.g., "48656C6C6F")
*
* @example
* ```ts
* bytesToHex(new Uint8Array([72, 101, 108, 108, 111])) // "48656C6C6F"
* ```
*/
function bytesToHex(bytes) {
	let hex = "";
	for (const byte of bytes) hex += HEX_TABLE[byte];
	return hex;
}
/**
* Convert a hex string to bytes.
*
* Whitespace is ignored. Odd-length strings are padded with trailing 0.
*
* @param hex - Hex string (e.g., "48656C6C6F" or "48 65 6C 6C 6F")
* @returns Decoded bytes
*
* @example
* ```ts
* hexToBytes("48656C6C6F") // Uint8Array([72, 101, 108, 108, 111])
* hexToBytes("ABC") // Uint8Array([171, 192]) - padded to "ABC0"
* ```
*/
function hexToBytes(hex) {
	const clean = hex.replace(/\s/g, "");
	const padded = clean.length % 2 === 1 ? `${clean}0` : clean;
	const bytes = new Uint8Array(padded.length / 2);
	for (let i = 0; i < bytes.length; i++) bytes[i] = Number.parseInt(padded.slice(i * 2, i * 2 + 2), 16);
	return bytes;
}
/**
* PDF whitespace characters (PDF spec 7.2.2)
*
* NUL, TAB, LF, FF, CR, SPACE
*/
const WHITESPACE = new Set([
	0,
	9,
	10,
	12,
	13,
	32
]);
/**
* PDF delimiter characters (PDF spec 7.2.2)
*
* ( ) < > [ ] { } / %
*/
const DELIMITERS = new Set([
	40,
	41,
	60,
	62,
	91,
	93,
	123,
	125,
	47,
	37
]);
/**
* Check if a byte is PDF whitespace.
*/
function isWhitespace$1(byte) {
	return WHITESPACE.has(byte);
}
/**
* Check if a byte is a PDF delimiter.
*/
function isDelimiter(byte) {
	return DELIMITERS.has(byte);
}
/**
* Check if a byte is a regular character (not whitespace or delimiter).
*/
function isRegularChar(byte) {
	return byte !== -1 && !WHITESPACE.has(byte) && !DELIMITERS.has(byte);
}
/**
* Check if a byte is an ASCII digit (0-9).
*/
function isDigit$1(byte) {
	return byte >= 48 && byte <= 57;
}
/**
* Check if a byte is a hex digit (0-9, A-F, a-f).
*/
function isHexDigit(byte) {
	return byte >= 48 && byte <= 57 || byte >= 65 && byte <= 70 || byte >= 97 && byte <= 102;
}
/**
* Get numeric value of a hex digit (0-15), or -1 if invalid.
*/
function hexValue(byte) {
	if (byte >= 48 && byte <= 57) return byte - 48;
	if (byte >= 65 && byte <= 70) return byte - 65 + 10;
	if (byte >= 97 && byte <= 102) return byte - 97 + 10;
	return -1;
}

//#region src/io/scanner.ts
/**
* Scanner - the lowest-level byte reader for PDF parsing.
*
* Wraps a Uint8Array and provides position tracking, peeking, and advancing.
* This is a minimal foundation for the lexer to build on.
*
* Key design decisions:
* - Returns -1 for EOF (classic C-style sentinel)
* - Backtracking via save/restore of position property
* - Clamps out-of-bounds positions instead of throwing
* - Bytes-only operations; PDF-specific concepts belong in the lexer
*/
var Scanner = class {
	/** The underlying byte array (readonly access) */
	bytes;
	/** Total number of bytes */
	length;
	/** Internal position tracking */
	_position = 0;
	constructor(bytes) {
		this.bytes = bytes;
		this.length = bytes.length;
	}
	/**
	* Current byte offset.
	* Readable and writable for backtracking.
	* Setting a value outside [0, length] clamps to that range.
	*/
	get position() {
		return this._position;
	}
	/**
	* True if position >= length (no more bytes to read).
	*/
	get isAtEnd() {
		return this._position >= this.length;
	}
	/**
	* Returns byte at current position, or -1 if at end.
	* Does not advance position.
	*/
	peek() {
		if (this._position >= this.length) return -1;
		const byte = this.bytes[this._position];
		if (byte === void 0) return -1;
		return byte;
	}
	/**
	* Returns byte at absolute offset, or -1 if out of bounds.
	* Does not affect position.
	*/
	peekAt(offset) {
		if (offset < 0 || offset >= this.length) return -1;
		const byte = this.bytes[offset];
		if (byte === void 0) return -1;
		return byte;
	}
	/**
	* Advances position by 1 and returns the byte that was at the old position.
	* Returns -1 and does not advance if already at end.
	*/
	advance() {
		if (this._position >= this.length) return -1;
		const byte = this.bytes[this._position];
		if (byte === void 0) return -1;
		this._position++;
		return byte;
	}
	/**
	* If current byte equals expected, advances and returns true.
	* Otherwise returns false without advancing.
	*/
	match(expected) {
		if (this._position >= this.length) return false;
		const byte = this.bytes[this._position];
		if (byte === void 0) return false;
		if (byte === expected) {
			this._position++;
			return true;
		}
		return false;
	}
	/**
	* Moves position to the given offset.
	* Clamps to valid range [0, length].
	*/
	moveTo(offset) {
		this._position = Math.max(0, Math.min(offset, this.length));
	}
};

//#region src/parser/token-reader.ts
/**
* Shared TextDecoder instance to avoid creating new ones in hot paths.
* TextDecoder is stateless when not using streaming mode, so it's safe to share.
*/
const sharedTextDecoder = new TextDecoder();
/**
* On-demand tokenizer for PDF syntax.
*
* Reads tokens one at a time from a Scanner, handling whitespace,
* comments, and lenient parsing of malformed input.
*/
var TokenReader = class {
	scanner;
	cachedToken = null;
	constructor(scanner) {
		this.scanner = scanner;
	}
	get position() {
		return this.scanner.position;
	}
	/**
	* Peek at the next token without consuming it.
	*/
	peekToken() {
		if (this.cachedToken === null) this.cachedToken = this.readToken();
		return this.cachedToken;
	}
	/**
	* Read and consume the next token.
	*/
	nextToken() {
		if (this.cachedToken !== null) {
			const token = this.cachedToken;
			this.cachedToken = null;
			return token;
		}
		return this.readToken();
	}
	/**
	* Skip whitespace and comments.
	*/
	skipWhitespaceAndComments() {
		while (true) {
			const byte = this.scanner.peek();
			if (byte === -1) return;
			if (WHITESPACE.has(byte)) {
				this.scanner.advance();
				continue;
			}
			if (byte === 37) {
				this.scanner.advance();
				this.skipToEndOfLine();
				continue;
			}
			return;
		}
	}
	/**
	* Peek at the next raw byte (after skipping whitespace/comments).
	*/
	peekByte() {
		this.skipWhitespaceAndComments();
		return this.scanner.peek();
	}
	skipToEndOfLine() {
		while (true) {
			const byte = this.scanner.peek();
			if (byte === -1 || byte === 10 || byte === 13) return;
			this.scanner.advance();
		}
	}
	readToken() {
		this.skipWhitespaceAndComments();
		const position = this.scanner.position;
		const byte = this.scanner.peek();
		if (byte === -1) return {
			type: "eof",
			position
		};
		if (byte === 47) return this.readName(position);
		if (byte === 40) return this.readLiteralString(position);
		if (byte === 60) return this.readAngleBracket(position);
		if (byte === 62) return this.readClosingAngle(position);
		if (byte === 91) {
			this.scanner.advance();
			return {
				type: "delimiter",
				value: "[",
				position
			};
		}
		if (byte === 93) {
			this.scanner.advance();
			return {
				type: "delimiter",
				value: "]",
				position
			};
		}
		if (this.isNumberStart(byte)) return this.readNumber(position);
		return this.readKeyword(position);
	}
	isNumberStart(byte) {
		return byte >= 48 && byte <= 57 || byte === 43 || byte === 45 || byte === 46;
	}
	readNumber(position) {
		const start = this.scanner.position;
		let hasDecimal = false;
		let hasDigit = false;
		let isNegative = false;
		const firstByte = this.scanner.peek();
		if (firstByte === 43 || firstByte === 45) {
			isNegative = firstByte === 45;
			this.scanner.advance();
			if (this.scanner.peek() === 45) {
				isNegative = false;
				while (this.scanner.peek() === 45) this.scanner.advance();
			}
		}
		const digitsStart = this.scanner.position;
		if (this.scanner.peek() === 46) {
			hasDecimal = true;
			this.scanner.advance();
		}
		while (true) {
			const byte = this.scanner.peek();
			if (isDigit$1(byte)) {
				hasDigit = true;
				this.scanner.advance();
				continue;
			}
			if (byte === 46 && !hasDecimal) {
				hasDecimal = true;
				this.scanner.advance();
				continue;
			}
			break;
		}
		if (hasDecimal) while (isDigit$1(this.scanner.peek())) {
			hasDigit = true;
			this.scanner.advance();
		}
		const end = this.scanner.position;
		if (!hasDigit) {
			while (isRegularChar(this.scanner.peek())) this.scanner.advance();
			return {
				type: "keyword",
				value: this.extractText(start, this.scanner.position),
				position
			};
		}
		const numericText = this.extractText(digitsStart, end);
		let value = parseFloat(numericText);
		if (isNegative) value = -value;
		return {
			type: "number",
			value,
			isInteger: !hasDecimal && Number.isInteger(value),
			position
		};
	}
	readName(position) {
		this.scanner.advance();
		const data = this.scanner.bytes;
		const start = this.scanner.position;
		let pos = start;
		const len = data.length;
		let hasEscape = false;
		while (pos < len) {
			const byte = data[pos];
			if (!isRegularChar(byte)) break;
			if (byte === 35) {
				hasEscape = true;
				break;
			}
			pos++;
		}
		if (!hasEscape) {
			this.scanner.moveTo(pos);
			let value = "";
			for (let i = start; i < pos; i++) value += String.fromCharCode(data[i]);
			return {
				type: "name",
				value,
				position
			};
		}
		this.scanner.moveTo(start);
		const bytes = [];
		while (true) {
			const byte = this.scanner.peek();
			if (!isRegularChar(byte)) break;
			this.scanner.advance();
			if (byte === 35) {
				const hex1 = this.scanner.peek();
				if (isHexDigit(hex1)) {
					this.scanner.advance();
					const hex2 = this.scanner.peek();
					if (isHexDigit(hex2)) {
						this.scanner.advance();
						const value = hexValue(hex1) << 4 | hexValue(hex2);
						bytes.push(value);
						continue;
					}
					bytes.push(35);
					bytes.push(hex1);
					continue;
				}
				bytes.push(35);
				continue;
			}
			bytes.push(byte);
		}
		return {
			type: "name",
			value: sharedTextDecoder.decode(new Uint8Array(bytes)),
			position
		};
	}
	readLiteralString(position) {
		this.scanner.advance();
		const bytes = [];
		let parenDepth = 1;
		while (parenDepth > 0) {
			const byte = this.scanner.peek();
			if (byte === -1) break;
			this.scanner.advance();
			if (byte === 40) {
				parenDepth++;
				bytes.push(byte);
				continue;
			}
			if (byte === 41) {
				parenDepth--;
				if (parenDepth > 0) bytes.push(byte);
				continue;
			}
			if (byte === 92) {
				const escaped = this.readEscapeSequence();
				if (escaped !== null) if (Array.isArray(escaped)) bytes.push(...escaped);
				else bytes.push(escaped);
				continue;
			}
			if (byte === 13) {
				if (this.scanner.peek() === 10) this.scanner.advance();
				bytes.push(10);
				continue;
			}
			bytes.push(byte);
		}
		return {
			type: "string",
			value: new Uint8Array(bytes),
			format: "literal",
			position
		};
	}
	readEscapeSequence() {
		const byte = this.scanner.peek();
		if (byte === -1) return null;
		this.scanner.advance();
		switch (byte) {
			case 110: return 10;
			case 114: return 13;
			case 116: return 9;
			case 98: return 8;
			case 102: return 12;
			case 40: return 40;
			case 41: return 41;
			case 92: return 92;
			case 13:
				if (this.scanner.peek() === 10) this.scanner.advance();
				return null;
			case 10: return null;
			default:
				if (byte >= 48 && byte <= 55) return this.readOctalEscape(byte);
				return byte;
		}
	}
	readOctalEscape(firstDigit) {
		let value = firstDigit - 48;
		let digits = 1;
		while (digits < 3) {
			const byte = this.scanner.peek();
			if (byte < 48 || byte > 55) break;
			this.scanner.advance();
			value = value << 3 | byte - 48;
			digits++;
		}
		return value & 255;
	}
	readAngleBracket(position) {
		this.scanner.advance();
		if (this.scanner.peek() === 60) {
			this.scanner.advance();
			return {
				type: "delimiter",
				value: "<<",
				position
			};
		}
		return this.readHexString(position);
	}
	readHexString(position) {
		const bytes = [];
		let pendingNibble = null;
		while (true) {
			const byte = this.scanner.peek();
			if (byte === -1 || byte === 62) {
				if (byte === 62) this.scanner.advance();
				break;
			}
			this.scanner.advance();
			if (WHITESPACE.has(byte)) continue;
			if (isHexDigit(byte)) {
				const nibble = hexValue(byte);
				if (pendingNibble === null) pendingNibble = nibble;
				else {
					bytes.push(pendingNibble << 4 | nibble);
					pendingNibble = null;
				}
			}
		}
		if (pendingNibble !== null) bytes.push(pendingNibble << 4);
		return {
			type: "string",
			value: new Uint8Array(bytes),
			format: "hex",
			position
		};
	}
	readClosingAngle(position) {
		this.scanner.advance();
		if (this.scanner.peek() === 62) {
			this.scanner.advance();
			return {
				type: "delimiter",
				value: ">>",
				position
			};
		}
		return {
			type: "delimiter",
			value: ">>",
			position
		};
	}
	readKeyword(position) {
		const start = this.scanner.position;
		while (isRegularChar(this.scanner.peek())) this.scanner.advance();
		return {
			type: "keyword",
			value: this.extractText(start, this.scanner.position),
			position
		};
	}
	extractText(start, end) {
		const bytes = this.scanner.bytes.subarray(start, end);
		return sharedTextDecoder.decode(bytes);
	}
};

//#region src/content/parsing/content-tokenizer.ts
/**
* Content stream tokenizer.
*
* Wraps TokenReader and converts tokens to ContentToken types.
* Post-processes keywords to distinguish operators from true/false/null.
*/
/**
* Tokenizer for PDF content streams.
*
* Wraps TokenReader to provide content-stream-specific token types.
* Distinguishes operators from keywords (true, false, null).
*/
var ContentTokenizer = class {
	scanner;
	reader;
	peeked = null;
	constructor(bytes) {
		this.scanner = new Scanner(bytes);
		this.reader = new TokenReader(this.scanner);
	}
	/**
	* Current position in the byte stream.
	*/
	get position() {
		return this.scanner.position;
	}
	/**
	* Access to underlying scanner for inline image handling.
	*/
	getScanner() {
		return this.scanner;
	}
	/**
	* Check if at end of stream.
	*/
	eof() {
		if (this.peeked !== null) return false;
		return this.reader.peekToken().type === "eof";
	}
	/**
	* Peek at next token without consuming.
	*/
	peek() {
		if (this.peeked !== null) return this.peeked;
		this.peeked = this.readNext();
		return this.peeked;
	}
	/**
	* Read and consume next token.
	*/
	nextToken() {
		if (this.peeked !== null) {
			const token = this.peeked;
			this.peeked = null;
			return token;
		}
		return this.readNext();
	}
	/**
	* Clear any peeked token (used after inline image data consumption).
	*/
	clearPeeked() {
		this.peeked = null;
	}
	readNext() {
		while (true) {
			const token = this.reader.nextToken();
			if (token.type === "eof") return null;
			const result = this.convertToken(token);
			if (result !== null) return result;
		}
	}
	convertToken(token) {
		switch (token.type) {
			case "number": return {
				type: "number",
				value: token.value
			};
			case "name": return {
				type: "name",
				value: token.value
			};
			case "string": return {
				type: "string",
				value: token.value,
				hex: token.format === "hex"
			};
			case "keyword": return this.handleKeyword(token.value);
			case "delimiter": return this.handleDelimiter(token.value);
			default: throw new Error(`Unexpected token type: ${token.type}`);
		}
	}
	handleKeyword(value) {
		if (value === "true") return {
			type: "bool",
			value: true
		};
		if (value === "false") return {
			type: "bool",
			value: false
		};
		if (value === "null") return { type: "null" };
		return {
			type: "operator",
			name: value
		};
	}
	handleDelimiter(value) {
		if (value === "[") return this.readArray();
		if (value === "<<") return this.readDict();
		console.warn(`ContentTokenizer: Unexpected delimiter '${value}' at position ${this.scanner.position} - skipping`);
		return null;
	}
	readArray() {
		const items = [];
		while (true) {
			const token = this.reader.peekToken();
			if (token.type === "eof") break;
			if (token.type === "delimiter" && token.value === "]") {
				this.reader.nextToken();
				break;
			}
			const item = this.nextToken();
			if (item === null) break;
			if (item.type === "operator") throw new Error(`Unexpected operator in array: ${item.name}`);
			items.push(item);
		}
		return {
			type: "array",
			items
		};
	}
	readDict() {
		const entries = /* @__PURE__ */ new Map();
		while (true) {
			const keyToken = this.reader.peekToken();
			if (keyToken.type === "eof") break;
			if (keyToken.type === "delimiter" && keyToken.value === ">>") {
				this.reader.nextToken();
				break;
			}
			const key = this.nextToken();
			if (key === null) break;
			if (key.type !== "name") throw new Error(`Expected name as dict key, got ${key.type}`);
			const value = this.nextToken();
			if (value === null) break;
			if (value.type === "operator") throw new Error(`Unexpected operator as dict value: ${value.name}`);
			entries.set(key.value, value);
		}
		return {
			type: "dict",
			entries
		};
	}
};

//#region src/content/parsing/operators.ts
/**
* All known PDF content stream operators.
*
* Based on PDF 1.7 specification tables.
*/
const KNOWN_OPERATORS = {
	q: {
		numArgs: 0,
		variableArgs: false
	},
	Q: {
		numArgs: 0,
		variableArgs: false
	},
	cm: {
		numArgs: 6,
		variableArgs: false
	},
	w: {
		numArgs: 1,
		variableArgs: false
	},
	J: {
		numArgs: 1,
		variableArgs: false
	},
	j: {
		numArgs: 1,
		variableArgs: false
	},
	M: {
		numArgs: 1,
		variableArgs: false
	},
	d: {
		numArgs: 2,
		variableArgs: false
	},
	ri: {
		numArgs: 1,
		variableArgs: false
	},
	i: {
		numArgs: 1,
		variableArgs: false
	},
	gs: {
		numArgs: 1,
		variableArgs: false
	},
	m: {
		numArgs: 2,
		variableArgs: false
	},
	l: {
		numArgs: 2,
		variableArgs: false
	},
	c: {
		numArgs: 6,
		variableArgs: false
	},
	v: {
		numArgs: 4,
		variableArgs: false
	},
	y: {
		numArgs: 4,
		variableArgs: false
	},
	h: {
		numArgs: 0,
		variableArgs: false
	},
	re: {
		numArgs: 4,
		variableArgs: false
	},
	S: {
		numArgs: 0,
		variableArgs: false
	},
	s: {
		numArgs: 0,
		variableArgs: false
	},
	f: {
		numArgs: 0,
		variableArgs: false
	},
	F: {
		numArgs: 0,
		variableArgs: false
	},
	"f*": {
		numArgs: 0,
		variableArgs: false
	},
	B: {
		numArgs: 0,
		variableArgs: false
	},
	"B*": {
		numArgs: 0,
		variableArgs: false
	},
	b: {
		numArgs: 0,
		variableArgs: false
	},
	"b*": {
		numArgs: 0,
		variableArgs: false
	},
	n: {
		numArgs: 0,
		variableArgs: false
	},
	W: {
		numArgs: 0,
		variableArgs: false
	},
	"W*": {
		numArgs: 0,
		variableArgs: false
	},
	Tc: {
		numArgs: 1,
		variableArgs: false
	},
	Tw: {
		numArgs: 1,
		variableArgs: false
	},
	Tz: {
		numArgs: 1,
		variableArgs: false
	},
	TL: {
		numArgs: 1,
		variableArgs: false
	},
	Tf: {
		numArgs: 2,
		variableArgs: false
	},
	Tr: {
		numArgs: 1,
		variableArgs: false
	},
	Ts: {
		numArgs: 1,
		variableArgs: false
	},
	BT: {
		numArgs: 0,
		variableArgs: false
	},
	ET: {
		numArgs: 0,
		variableArgs: false
	},
	Td: {
		numArgs: 2,
		variableArgs: false
	},
	TD: {
		numArgs: 2,
		variableArgs: false
	},
	Tm: {
		numArgs: 6,
		variableArgs: false
	},
	"T*": {
		numArgs: 0,
		variableArgs: false
	},
	Tj: {
		numArgs: 1,
		variableArgs: false
	},
	TJ: {
		numArgs: 1,
		variableArgs: false
	},
	"'": {
		numArgs: 1,
		variableArgs: false
	},
	"\"": {
		numArgs: 3,
		variableArgs: false
	},
	CS: {
		numArgs: 1,
		variableArgs: false
	},
	cs: {
		numArgs: 1,
		variableArgs: false
	},
	SC: {
		numArgs: 4,
		variableArgs: true
	},
	SCN: {
		numArgs: 5,
		variableArgs: true
	},
	sc: {
		numArgs: 4,
		variableArgs: true
	},
	scn: {
		numArgs: 5,
		variableArgs: true
	},
	G: {
		numArgs: 1,
		variableArgs: false
	},
	g: {
		numArgs: 1,
		variableArgs: false
	},
	RG: {
		numArgs: 3,
		variableArgs: false
	},
	rg: {
		numArgs: 3,
		variableArgs: false
	},
	K: {
		numArgs: 4,
		variableArgs: false
	},
	k: {
		numArgs: 4,
		variableArgs: false
	},
	Do: {
		numArgs: 1,
		variableArgs: false
	},
	MP: {
		numArgs: 1,
		variableArgs: false
	},
	DP: {
		numArgs: 2,
		variableArgs: false
	},
	BMC: {
		numArgs: 1,
		variableArgs: false
	},
	BDC: {
		numArgs: 2,
		variableArgs: false
	},
	EMC: {
		numArgs: 0,
		variableArgs: false
	},
	BI: {
		numArgs: 0,
		variableArgs: false
	},
	ID: {
		numArgs: 0,
		variableArgs: false
	},
	EI: {
		numArgs: 0,
		variableArgs: false
	},
	sh: {
		numArgs: 1,
		variableArgs: false
	},
	BX: {
		numArgs: 0,
		variableArgs: false
	},
	EX: {
		numArgs: 0,
		variableArgs: false
	}
};

//#region src/content/parsing/inline-image.ts
/**
* Inline image EI (end inline image) detection.
*
* Ported from pdf.js's battle-tested implementation.
* Handles filter-specific detection and heuristic fallback.
*/
const E$1 = 69;
const I = 73;
const TILDE = 126;
const GT = 62;
/**
* Find the end of inline image data and extract it.
*
* Uses filter-specific detection when possible, falls back to heuristic.
*/
function extractInlineImageData(scanner, params) {
	const warnings = [];
	const firstByte = scanner.peek();
	if (firstByte === 32 || firstByte === 10 || firstByte === 13) {
		scanner.advance();
		if (firstByte === 13 && scanner.peek() === 10) scanner.advance();
	}
	const startPos = scanner.position;
  const exact = exactInlineImageEnd(scanner, params);
  if (exact) return { data: exact, warnings };
	const filter = params.get("F") ?? params.get("Filter");
	if (filter?.type === "name") {
		const filterName = filter.value;
		if (filterName === "DCT" || filterName === "DCTDecode") {
			const result = findDCTDecodeEnd(scanner, startPos);
			if (result !== null) {
				skipToEI(scanner);
				return {
					data: result,
					warnings
				};
			}
			warnings.push("DCT EOI marker not found, using heuristic detection");
			scanner.moveTo(startPos);
		}
		if (filterName === "A85" || filterName === "ASCII85Decode") {
			const result = findASCII85DecodeEnd(scanner, startPos);
			if (result !== null) {
				skipToEI(scanner);
				return {
					data: result,
					warnings
				};
			}
			warnings.push("ASCII85 EOD marker not found, using heuristic detection");
			scanner.moveTo(startPos);
		}
		if (filterName === "AHx" || filterName === "ASCIIHexDecode") {
			const result = findASCIIHexDecodeEnd(scanner, startPos);
			if (result !== null) {
				skipToEI(scanner);
				return {
					data: result,
					warnings
				};
			}
			warnings.push("ASCIIHex EOD marker not found, using heuristic detection");
			scanner.moveTo(startPos);
		}
	}
	return {
		data: findDefaultInlineStreamEnd(scanner, startPos, warnings),
		warnings
	};
}
/**
* Find the EOI (end-of-image) marker 0xFFD9 for DCT/JPEG.
*/
function findDCTDecodeEnd(scanner, startPos) {
	let foundEOI = false;
	while (!scanner.isAtEnd) {
		if (scanner.advance() !== 255) continue;
		switch (scanner.peek()) {
			case 0:
				scanner.advance();
				break;
			case 255: break;
			case 217:
				scanner.advance();
				foundEOI = true;
				break;
			case 192:
			case 193:
			case 194:
			case 195:
			case 197:
			case 198:
			case 199:
			case 201:
			case 202:
			case 203:
			case 205:
			case 206:
			case 207:
			case 196:
			case 204:
			case 218:
			case 219:
			case 220:
			case 221:
			case 222:
			case 223:
			case 224:
			case 225:
			case 226:
			case 227:
			case 228:
			case 229:
			case 230:
			case 231:
			case 232:
			case 233:
			case 234:
			case 235:
			case 236:
			case 237:
			case 238:
			case 239:
			case 254: {
				scanner.advance();
				const len1 = scanner.advance();
				const len2 = scanner.advance();
				if (len1 === -1 || len2 === -1) break;
				const markerLength = len1 << 8 | len2;
				if (markerLength > 2) for (let i = 0; i < markerLength - 2 && !scanner.isAtEnd; i++) scanner.advance();
				break;
			}
			default:
				scanner.advance();
				break;
		}
		if (foundEOI) break;
	}
	if (!foundEOI) return null;
	return scanner.bytes.subarray(startPos, scanner.position);
}
/**
* Find the EOD marker '~>' for ASCII85.
*/
function findASCII85DecodeEnd(scanner, startPos) {
	while (!scanner.isAtEnd) if (scanner.advance() === TILDE) {
		const tildePos = scanner.position;
		while (isWhitespace$1(scanner.peek())) scanner.advance();
		if (scanner.peek() === GT) {
			scanner.advance();
			return scanner.bytes.subarray(startPos, scanner.position);
		}
		if (scanner.position > tildePos) {
			const b1 = scanner.peek();
			const b2 = scanner.peekAt(scanner.position + 1);
			if (b1 === E$1 && b2 === I) return scanner.bytes.subarray(startPos, tildePos);
		}
	}
	return null;
}
/**
* Find the EOD marker '>' for ASCIIHex.
*/
function findASCIIHexDecodeEnd(scanner, startPos) {
	while (!scanner.isAtEnd) if (scanner.advance() === GT) return scanner.bytes.subarray(startPos, scanner.position);
	return null;
}
/**
* Default heuristic detection for finding EI.
*
* Scans for "EI" followed by whitespace, then validates:
* 1. Next bytes are ASCII (not binary)
* 2. A valid PDF operator follows
*/
function findDefaultInlineStreamEnd(scanner, startPos, warnings) {
	const n = 15;
	let state = 0;
	let maybeEIPos = null;
	while (!scanner.isAtEnd) {
		const ch = scanner.advance();
		if (state === 0) state = ch === E$1 ? 1 : 0;
		else if (state === 1) state = ch === I ? 2 : 0;
		else {
			if (ch === 32 || ch === 10 || ch === 13) {
				maybeEIPos = scanner.position;
				if (!checkFollowingBytesAreAscii(scanner, n)) {
					state = 0;
					continue;
				}
				if (!validateFollowingOperator(scanner)) {
					state = 0;
					continue;
				}
				const dataEnd = scanner.position - 3;
				return scanner.bytes.subarray(startPos, dataEnd);
			}
			state = 0;
		}
	}
	warnings.push("Reached end of stream without finding valid EI marker");
	if (maybeEIPos !== null) {
		warnings.push("Recovering using last potential EI position");
		const dataEnd = maybeEIPos - 3;
		return scanner.bytes.subarray(startPos, Math.max(startPos, dataEnd));
	}
	return scanner.bytes.subarray(startPos, scanner.position);
}
/**
* Check that the next n bytes are ASCII (printable or line breaks).
*/
function checkFollowingBytesAreAscii(scanner, n) {
	const pos = scanner.position;
	for (let i = 0; i < n; i++) {
		const ch = scanner.peekAt(pos + i);
		if (ch === -1) break;
		if (ch === 0) {
			if (scanner.peekAt(pos + i + 1) !== 0) continue;
			return false;
		}
		if (ch !== 10 && ch !== 13 && (ch < 32 || ch > 127)) return false;
	}
	return true;
}
/**
* Validate that a known PDF operator follows.
*
* Parses ahead to find an operator and checks argument count.
*/
function validateFollowingOperator(scanner) {
	const pos = scanner.position;
	const lookahead = 75;
	let numArgs = 0;
	let i = 0;
	while (i < lookahead) {
		while (i < lookahead) {
			const ch = scanner.peekAt(pos + i);
			if (ch === -1) return false;
			if (!isWhitespace$1(ch)) break;
			i++;
		}
		if (i >= lookahead) return false;
		const tokenStart = pos + i;
		const firstCh = scanner.peekAt(tokenStart);
		if (firstCh === -1) return false;
		if (isNumberStart$1(firstCh)) {
			i++;
			while (i < lookahead) {
				const ch = scanner.peekAt(pos + i);
				if (ch === -1 || isWhitespace$1(ch) || isDelimiter(ch)) break;
				i++;
			}
			numArgs++;
			continue;
		}
		if (firstCh === 47) {
			i++;
			while (i < lookahead) {
				const ch = scanner.peekAt(pos + i);
				if (ch === -1 || isWhitespace$1(ch) || isDelimiter(ch)) break;
				i++;
			}
			numArgs++;
			continue;
		}
		if (firstCh === 40 || firstCh === 60) {
			i++;
			numArgs++;
			continue;
		}
		if (firstCh === 91 || firstCh === 60 && scanner.peekAt(pos + i + 1) === 60) {
			i++;
			numArgs++;
			continue;
		}
		const wordStart = i;
		while (i < lookahead) {
			const ch = scanner.peekAt(pos + i);
			if (ch === -1 || isWhitespace$1(ch) || isDelimiter(ch)) break;
			i++;
		}
		const wordBytes = [];
		for (let j = wordStart; j < i; j++) {
			const ch = scanner.peekAt(pos + j);
			if (ch !== -1) wordBytes.push(ch);
		}
		const word = String.fromCharCode(...wordBytes);
		if (word === "true" || word === "false" || word === "null") {
			numArgs++;
			continue;
		}
		const opInfo = KNOWN_OPERATORS[word];
		if (!opInfo) return false;
		if (opInfo.variableArgs) {
			if (numArgs <= opInfo.numArgs) return true;
		} else if (numArgs === opInfo.numArgs) return true;
		numArgs = 0;
	}
	return false;
}
function isNumberStart$1(ch) {
	return ch >= 48 && ch <= 57 || ch === 43 || ch === 45 || ch === 46;
}
/**
* Skip past the EI operator after filter-specific detection.
*/
function skipToEI(scanner) {
	let state = 0;
	while (!scanner.isAtEnd) {
		const ch = scanner.advance();
		if (state === 0) {
			state = ch === E$1 ? 1 : 0;
			continue;
		}
		if (state === 1) {
			state = ch === I ? 2 : 0;
			continue;
		}
		break;
	}
}

//#region src/content/parsing/content-stream-parser.ts
/**
* Content stream parser.
*
* Parses PDF content streams into structured operations.
*/
/**
* Parser for PDF content streams.
*
* Converts a byte stream into an array of parsed operations,
* each bundling an operator with its preceding operands.
*/
var ContentStreamParser = class {
	tokenizer;
	warnings = [];
	constructor(bytes) {
		this.tokenizer = new ContentTokenizer(bytes);
	}
	/**
	* Parse all operations from the content stream.
	*/
	parse() {
		const operations = [];
		const operands = [];
		while (!this.tokenizer.eof()) {
			const token = this.tokenizer.nextToken();
			if (token === null) break;
			if (token.type === "operator") {
				if (token.name === "BI") {
					const inlineImage = this.parseInlineImage();
					operations.push(inlineImage);
				} else operations.push({
					operator: token.name,
					operands: [...operands]
				});
				operands.length = 0;
			} else operands.push(token);
		}
		if (operands.length > 0) this.warnings.push(`Content stream has ${operands.length} trailing operand(s) without operator`);
		return {
			operations,
			warnings: this.warnings
		};
	}
	/**
	* Iterate operations lazily.
	*/
	*[Symbol.iterator]() {
		const operands = [];
		while (!this.tokenizer.eof()) {
			const token = this.tokenizer.nextToken();
			if (token === null) break;
			if (token.type === "operator") {
				if (token.name === "BI") yield this.parseInlineImage();
				else yield {
					operator: token.name,
					operands: [...operands]
				};
				operands.length = 0;
			} else operands.push(token);
		}
	}
	parseInlineImage() {
		const params = /* @__PURE__ */ new Map();
		while (!this.tokenizer.eof()) {
			const token = this.tokenizer.nextToken();
			if (token === null) {
				this.warnings.push("Unexpected EOF in inline image parameters");
				break;
			}
			if (token.type === "operator") {
				if (token.name === "ID") break;
				this.warnings.push(`Unexpected operator in inline image params: ${token.name}`);
				continue;
			}
			if (token.type !== "name") {
				this.warnings.push(`Expected name as inline image param key, got ${token.type}`);
				continue;
			}
			const key = token.value;
			const value = this.tokenizer.nextToken();
			if (value === null) {
				this.warnings.push("Unexpected EOF reading inline image param value");
				break;
			}
			if (value.type === "operator") {
				if (value.name === "ID") {
					this.warnings.push(`Missing value for inline image param: ${key}`);
					break;
				}
				this.warnings.push(`Unexpected operator as param value: ${value.name}`);
				continue;
			}
			params.set(key, value);
		}
		const { data, warnings } = extractInlineImageData(this.tokenizer.getScanner(), params);
		this.warnings.push(...warnings);
		this.tokenizer.clearPeeked();
		return {
			operator: "BI",
			params,
			data
		};
	}
};

export { ContentStreamParser };
